import React, { useState, useRef } from 'react';
import { 
  FileText, 
  Download, 
  FileSpreadsheet, 
  Mail, 
  ExternalLink, 
  CheckCircle2, 
  Loader2, 
  ChevronDown, 
  ChevronUp,
  Table,
  Sparkles
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

export default function CopilotReportCard({ reportData, onActionComplete }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (!reportData) return null;

  const {
    type = 'REPORT',
    title = 'Enterprise Report',
    summaryMetrics = [],
    columns = [],
    rows = [],
    actions = []
  } = reportData;

  const rowLimit = expanded ? rows.length : Math.min(rows.length, 5);

  // Format column headers nicely (e.g., "account_name" -> "Account Name")
  const formatHeader = (col) => {
    if (!col) return '';
    return col
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // 1. Download CSV
  const handleDownloadCSV = () => {
    try {
      if (!rows.length || !columns.length) {
        toast.error('No tabular rows available to export.');
        return;
      }

      const headers = columns.map(formatHeader);
      const csvRows = rows.map((row) =>
        columns.map((col) => {
          const val = row[col] !== undefined && row[col] !== null ? row[col] : '';
          const strVal = String(val);
          return strVal.includes(',') || strVal.includes('"') || strVal.includes('\n')
            ? `"${strVal.replace(/"/g, '""')}"`
            : strVal;
        }).join(',')
      );

      const csvContent = '\ufeff' + [headers.join(','), ...csvRows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${title.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported CSV: ${title}`);
      if (onActionComplete) onActionComplete('download_csv');
    } catch (err) {
      console.error('CSV export error:', err);
      toast.error('Failed to export CSV');
    }
  };

  // 2. Download PDF via html2pdf
  const handleDownloadPDF = async () => {
    try {
      setIsExportingPDF(true);
      const { default: html2pdf } = await import('html2pdf.js');

      // Create a clean printable container offscreen
      const printContainer = document.createElement('div');
      printContainer.style.padding = '24px';
      printContainer.style.fontFamily = 'system-ui, -apple-system, sans-serif';
      printContainer.style.color = '#1e293b';
      printContainer.style.backgroundColor = '#ffffff';

      // Company header & title
      let html = `
        <div style="border-bottom: 2px solid #4f46e5; padding-bottom: 12px; margin-bottom: 16px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <h2 style="margin: 0; color: #1e1b4b; font-size: 20px; font-weight: 800;">Global AI ERP — Executive Report</h2>
            <span style="font-size: 11px; color: #64748b;">${new Date().toLocaleString()}</span>
          </div>
          <h3 style="margin: 4px 0 0 0; color: #4338ca; font-size: 15px; font-weight: 700;">${title}</h3>
        </div>
      `;

      // Metrics block
      if (summaryMetrics.length > 0) {
        html += `<div style="display: flex; gap: 12px; margin-bottom: 16px;">`;
        summaryMetrics.forEach((m) => {
          html += `
            <div style="flex: 1; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px;">
              <div style="font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase;">${m.label}</div>
              <div style="font-size: 14px; font-weight: 800; color: #0f172a; margin-top: 2px;">${m.value}</div>
            </div>
          `;
        });
        html += `</div>`;
      }

      // Table block
      if (columns.length > 0 && rows.length > 0) {
        html += `
          <table style="width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 8px;">
            <thead>
              <tr style="background-color: #f1f5f9; text-align: left; color: #334155;">
                ${columns.map((c) => `<th style="padding: 8px; border: 1px solid #cbd5e1; font-weight: 700;">${formatHeader(c)}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${rows.map((r, i) => `
                <tr style="background-color: ${i % 2 === 0 ? '#ffffff' : '#f8fafc'};">
                  ${columns.map((c) => `<td style="padding: 7px 8px; border: 1px solid #e2e8f0;">${r[c] !== undefined && r[c] !== null ? r[c] : '-'}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;
      }

      // Footer
      html += `
        <div style="margin-top: 24px; padding-top: 10px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #94a3b8; display: flex; justify-content: space-between;">
          <span>Generated by Savvy AI Enterprise Copilot</span>
          <span>Confidential — Internal Use Only</span>
        </div>
      `;

      printContainer.innerHTML = html;
      document.body.appendChild(printContainer);

      const opt = {
        margin: [8, 8, 8, 8],
        filename: `${title.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: columns.length > 5 ? 'landscape' : 'portrait' }
      };

      await html2pdf().from(printContainer).set(opt).save();
      document.body.removeChild(printContainer);

      toast.success(`Exported PDF: ${title}`);
      if (onActionComplete) onActionComplete('download_pdf');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to export PDF');
    } finally {
      setIsExportingPDF(false);
    }
  };

  // 3. Email Report to User
  const handleSendEmail = async () => {
    try {
      setIsSendingEmail(true);
      const res = await api.post('/intelligence/email-report', {
        reportData,
        targetEmail: user?.email
      });

      if (res.data?.status === 'success') {
        setEmailSent(true);
        toast.success(`Report dispatched to ${res.data.data?.targetEmail || user?.email}`);
        if (onActionComplete) onActionComplete('email_report');
      } else {
        toast.error(res.data?.message || 'Failed to dispatch report email.');
      }
    } catch (err) {
      console.error('Email dispatch error:', err);
      toast.error(err.response?.data?.message || 'Failed to send report email.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // 4. Open in Dashboard
  const handleOpenDashboard = (path) => {
    if (path) {
      navigate(path);
      if (onActionComplete) onActionComplete('open_dashboard');
    }
  };

  return (
    <div className="w-full mt-2 rounded-xl bg-white border border-indigo-100 shadow-sm overflow-hidden text-xs">
      {/* Top Banner / Title */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 px-3.5 py-2.5 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-lg bg-indigo-500/20 text-indigo-300 border border-indigo-400/30">
            <Sparkles className="w-3.5 h-3.5" />
          </div>
          <div>
            <span className="font-bold text-[12px] block text-white tracking-tight">{title}</span>
            <span className="text-[10px] text-indigo-300 font-semibold">{type.replace(/_/g, ' ')}</span>
          </div>
        </div>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
          Live Database
        </span>
      </div>

      {/* Summary KPI Cards */}
      {summaryMetrics.length > 0 && (
        <div className="p-3 bg-slate-50/80 border-b border-slate-100 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {summaryMetrics.map((metric, mIdx) => (
            <div 
              key={mIdx}
              className="bg-white p-2 rounded-lg border border-slate-200/80 shadow-2xs flex flex-col justify-between"
            >
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider truncate">
                {metric.label}
              </span>
              <span className="text-xs font-black text-slate-900 mt-1 truncate">
                {metric.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Table Preview */}
      {columns.length > 0 && rows.length > 0 ? (
        <div className="p-2">
          <div className="overflow-x-auto rounded-lg border border-slate-200 stylish-scrollbar max-h-48">
            <table className="w-full text-[11px] text-left">
              <thead className="bg-slate-100/90 text-slate-700 font-bold sticky top-0 border-b border-slate-200">
                <tr>
                  {columns.map((col, cIdx) => (
                    <th key={cIdx} className="px-2.5 py-1.5 whitespace-nowrap">
                      {formatHeader(col)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.slice(0, rowLimit).map((row, rIdx) => (
                  <tr key={rIdx} className="hover:bg-slate-50/80 transition-colors">
                    {columns.map((col, cIdx) => {
                      const val = row[col];
                      const isNum = typeof val === 'number' || (!isNaN(val) && val !== '' && !isNaN(parseFloat(val)));
                      return (
                        <td 
                          key={cIdx} 
                          className={clsx(
                            "px-2.5 py-1.5 text-slate-700 whitespace-nowrap",
                            isNum && "font-mono"
                          )}
                        >
                          {val !== undefined && val !== null ? String(val) : '-'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Toggle Expand Rows */}
          {rows.length > 5 && (
            <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-500 px-1">
              <span>Showing {rowLimit} of {rows.length} records</span>
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="inline-flex items-center gap-1 font-bold text-indigo-600 hover:text-indigo-700 cursor-pointer"
              >
                <span>{expanded ? 'Show Less' : 'Show All Rows'}</span>
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="p-3 text-center text-[11px] text-slate-500 font-medium">
          No records found matching current query parameters.
        </div>
      )}

      {/* Action Bar (Download PDF / Download CSV / Email / Open in Dashboard) */}
      <div className="p-2.5 bg-slate-50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          {/* Download PDF */}
          <button
            type="button"
            onClick={handleDownloadPDF}
            disabled={isExportingPDF}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-[10px] shadow-xs hover:shadow transition-all cursor-pointer disabled:opacity-50"
            title="Download formatted PDF report"
          >
            {isExportingPDF ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            <span>PDF</span>
          </button>

          {/* Download CSV */}
          <button
            type="button"
            onClick={handleDownloadCSV}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 font-bold text-[10px] shadow-2xs hover:shadow-xs transition-all cursor-pointer"
            title="Download CSV spreadsheet"
          >
            <FileSpreadsheet className="w-3 h-3 text-emerald-600" />
            <span>CSV</span>
          </button>

          {/* Email to User */}
          <button
            type="button"
            onClick={handleSendEmail}
            disabled={isSendingEmail || emailSent}
            className={clsx(
              "inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-bold text-[10px] border shadow-2xs transition-all cursor-pointer disabled:opacity-60",
              emailSent
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-white hover:bg-slate-100 text-slate-700 border-slate-200 hover:text-indigo-600"
            )}
            title={`Email report to ${user?.email || 'your registered email'}`}
          >
            {isSendingEmail ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : emailSent ? (
              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
            ) : (
              <Mail className="w-3 h-3 text-blue-600" />
            )}
            <span>{emailSent ? 'Sent to Email' : 'Email Me'}</span>
          </button>
        </div>

        {/* Dashboard Deep Links */}
        {Array.isArray(actions) && actions.length > 0 && (
          <div className="flex items-center gap-1">
            {actions.map((act, aIdx) => (
              <button
                key={aIdx}
                type="button"
                onClick={() => handleOpenDashboard(act.path)}
                className="inline-flex items-center gap-1 text-[10px] font-bold text-indigo-700 hover:text-indigo-900 bg-indigo-50/80 hover:bg-indigo-100 px-2 py-1 rounded-md border border-indigo-200/60 transition-colors cursor-pointer"
              >
                <span>{act.label}</span>
                <ExternalLink className="w-3 h-3" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
